"use client";

import { useToast } from "@/components/providers/FetchToastProvider";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchPill } from "@/fetch-ui/FetchPill";
import type { FormattedLocation } from "@/hooks/useLocationSearch";
import { getPlaceDetails } from "@/lib/database/server/place-actions";
import { PLACE_FIELD_MASKS } from "@/lib/database/server/place-field-masks";
import { cn } from "@/lib/utils/index";
import { Building2, Search } from "lucide-react";
import { useState } from "react";
import { useOfficeSuggestions } from "../../_context/OfficeSuggestionsContext";
import { VISIBLE_OFFICE_SUGGESTIONS } from "../../_logic/constants";
import { locationsMatch, officeMatchesQuery } from "../../_logic/location-utils";
import { CreateEventFormData, OfficeSuggestion, WizardAction } from "../../_types/wizard-types";
import { SuggestionPill } from "./SuggestionPill";

interface OfficeSuggestionsProps {
  formData: CreateEventFormData;
  dispatch: React.Dispatch<WizardAction>;
  /** Max number of locations the step allows (matches the autocomplete's limit). */
  limit: number;
}

/**
 * Looks up an office's coordinates so the venue preview searches around the office itself rather
 * than free-text matching its street address.
 *
 * @param location - The office as a selectable location.
 * @param googlePlaceId - The office's Google place.
 * @returns The office location with real coordinates, or unchanged when the lookup fails.
 */
async function withCoordinates(location: FormattedLocation, googlePlaceId: string): Promise<FormattedLocation> {
  try {
    const details = await getPlaceDetails(googlePlaceId, [PLACE_FIELD_MASKS.ESSENTIALS.location]);
    const { latitude, longitude } = details.location ?? {};
    if (!latitude || !longitude) return location;
    return { ...location, coordinates: { lat: latitude, lng: longitude } };
  } catch (error) {
    console.error("OfficeSuggestions: failed to look up office coordinates", error);
    return location;
  }
}

/**
 * A "Your offices" row of one-click pills for the enterprise's stored office locations, shown on the
 * location step above the popular cities. Renders nothing when the enterprise has no offices. Picking
 * an office adds it at once (pending) and swaps in its coordinates when the lookup resolves; if the
 * lookup fails the office stays selected and is searched by address. Long lists collapse behind
 * "+N more"; expanding reveals a search box over a height-capped, scrolling pill area, so an
 * enterprise with hundreds of offices can find one without the step growing past the buttons.
 *
 * @param props - The wizard form data, dispatch, and selection limit.
 * @returns The offices row, or `null` when there are none.
 */
export const OfficeSuggestions = ({ formData, dispatch, limit }: OfficeSuggestionsProps) => {
  // Hooks
  const offices = useOfficeSuggestions();
  const { showToast } = useToast();

  // States
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");

  // Derived values
  const atLimit = formData.formattedLocations.length >= limit;
  const hiddenCount = offices.length - VISIBLE_OFFICE_SUGGESTIONS;
  const visibleOffices = expanded
    ? offices.filter((office) => officeMatchesQuery(office, query))
    : offices.slice(0, VISIBLE_OFFICE_SUGGESTIONS);
  const findSelected = (office: OfficeSuggestion) =>
    formData.formattedLocations.find((loc) => locationsMatch(loc, office.location));

  // Handlers
  const handleToggle = async (office: OfficeSuggestion) => {
    const existing = findSelected(office);
    if (existing) {
      dispatch({ type: "TOGGLE_LOCATION", payload: { location: existing, limit } });
      return;
    }

    if (atLimit) {
      showToast(`You can select up to ${limit} locations.`, "warning");
      return;
    }

    if (!office.googlePlaceId) {
      dispatch({ type: "TOGGLE_LOCATION", payload: { location: office.location, limit } });
      return;
    }

    dispatch({ type: "TOGGLE_LOCATION", payload: { location: { ...office.location, pending: true }, limit } });
    const resolved = await withCoordinates(office.location, office.googlePlaceId);
    dispatch({ type: "RESOLVE_LOCATION", payload: { placeId: office.location.place_id, location: resolved } });
  };

  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
    setQuery("");
  };

  // Render
  if (offices.length === 0) return null;

  // Rendered inside the pill row when collapsed, but below the scroll area when expanded so it can't
  // scroll out of reach.
  const expandToggle = hiddenCount > 0 && (
    <FetchPill
      type="button"
      size="sm"
      aria-expanded={expanded}
      onClick={handleToggleExpanded}
      className="self-start px-3 py-1 font-medium text-gray-500"
    >
      {expanded ? "Show fewer" : `+${hiddenCount} more`}
    </FetchPill>
  );

  return (
    <div className="mt-5 flex flex-col gap-2.5">
      <span id="office-suggestions-label" className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
        <Building2 className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
        Your offices
      </span>
      {expanded && (
        // Focused on mount: finding an office is the reason to open the full list.
        <FetchInput
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your offices"
          aria-label="Search your offices"
          leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
        />
      )}
      <div
        role="group"
        aria-labelledby="office-suggestions-label"
        className={cn(
          "flex flex-wrap gap-2",
          expanded && "max-h-44 overflow-y-auto rounded-2xl border border-gray-200 p-3"
        )}
      >
        {visibleOffices.map((office) => {
          const active = !!findSelected(office);
          return (
            <SuggestionPill
              key={`${office.location.place_id}:${office.label}`}
              label={office.label}
              active={active}
              limitBlocked={atLimit && !active}
              onClick={() => handleToggle(office)}
            />
          );
        })}
        {expanded && visibleOffices.length === 0 && (
          <p className="text-sm font-medium text-gray-500">No offices match &ldquo;{query.trim()}&rdquo;</p>
        )}
        {!expanded && expandToggle}
      </div>
      {expanded && expandToggle}
    </div>
  );
};
